/**
 * Centralized contact info for Miller 40.
 *
 * `primary` used to intentionally DISPLAY one number and DIAL another (a
 * net2phone tracked line) so net2phone could capture call analytics. That
 * split is retired (WSP-843): net2phone is out of the web-call path entirely,
 * and `primary` now displays and dials the same brand number, matching the
 * direct-dial pattern already used by collision/calleOcho/shop/parts below.
 *
 * Ad-attributed call tracking now lives OUTSIDE this file: Google Forwarding
 * Numbers (GFN), configured in Google Ads/GTM, swap the DOM number for
 * ad-visitors at runtime by matching the `data-phone` hook — no code-level
 * display/dial split is needed or wanted for that.
 *
 * Override per environment with:
 *   NEXT_PUBLIC_DISPLAY_PHONE       (visible + dialed number, e.g. "786-801-0234")
 */

// ---- primary: brand number, shown and dialed directly (net2phone retired, WSP-843) ----
const DEFAULT_DISPLAY_PHONE = "786-801-0234";

const displayRaw =
  process.env.NEXT_PUBLIC_DISPLAY_PHONE ?? DEFAULT_DISPLAY_PHONE;

// ---- direct-dial lines ----
// Each has ONE override; its E.164 is derived from it (toE164), so display and
// dial can never desync (WSP-832). Same pattern `primary` now follows since
// net2phone was retired (WSP-843). Ad-attribution swapping is handled
// entirely externally by GFN via the `data-phone` hook, not in this file.
const DEFAULT_COLLISION_PHONE = "305-707-4123";
const DEFAULT_CALLE_OCHO_PHONE = "786-305-4040";
const DEFAULT_SHOP_PHONE = "305-337-2787";
// Parts intentionally shares the Bird Rd shop line: the shop and the parts
// counter answer the same number today (confirmed with PM, WSP-1191). Override
// with NEXT_PUBLIC_PARTS_PHONE if Parts ever gets its own line.
const DEFAULT_PARTS_PHONE = "305-337-2787";

const collisionRaw =
  process.env.NEXT_PUBLIC_COLLISION_PHONE ?? DEFAULT_COLLISION_PHONE;
const calleOchoRaw =
  process.env.NEXT_PUBLIC_CALLE_OCHO_PHONE ?? DEFAULT_CALLE_OCHO_PHONE;
const shopRaw =
  process.env.NEXT_PUBLIC_SHOP_PHONE ?? DEFAULT_SHOP_PHONE;
const partsRaw =
  process.env.NEXT_PUBLIC_PARTS_PHONE ?? DEFAULT_PARTS_PHONE;

// "786-801-0234" -> "(786) 801-0234"
export function formatUsPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^1/, "");
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// "305-707-4123" -> "+13057074123" (US E.164)
function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^1/, "");
  return `+1${digits}`;
}

/** Stable `data-phone` values — the selectors the GFN snippet swaps against. */
export const PHONE_KEYS = ["primary", "collision", "calleOcho", "shop", "parts"] as const;
export type PhoneKey = (typeof PHONE_KEYS)[number];

export interface PhoneNumber {
  /** `data-phone` value; the DOM hook GFN swaps against (WSP-828 Phase 2). */
  key: PhoneKey;
  /** Brand number shown to users, e.g. "+1 786-801-0234". Always link via telHref/e164 — never build a tel: from this string directly. */
  display: string;
  /** Anchor href derived from e164, e.g. "tel:+17868010234". Every line dials exactly what it displays (net2phone retired, WSP-843) — this stays the single source of truth for what gets dialed if a tracked/distinct dial line is ever reintroduced for a specific line. */
  telHref: string;
  /** E.164 of the dialed number, currently derived directly from display for every line (WSP-843); telHref is built from this, never from display. */
  e164: string;
}

/**
 * The ONE place a PhoneNumber is built. telHref is DERIVED from e164 here and
 * nowhere else, so a display string can never leak into an href — the failure
 * mode this whole module exists to prevent (WSP-832).
 */
function makePhone(key: PhoneKey, rawDisplay: string, e164: string): PhoneNumber {
  return { key, display: `+1 ${rawDisplay}`, telHref: `tel:${e164}`, e164 };
}

/**
 * Every phone number on the public site, keyed by its `data-phone` hook. Any
 * surface that renders a number reads it from here: the display for the visible
 * text, the telHref for the anchor. No component may construct a tel: from a
 * display string.
 */
const phones = {
  // Net2phone retired (WSP-843): primary now dials exactly what it displays,
  // same pattern as every line below.
  primary: makePhone("primary", displayRaw, toE164(displayRaw)),
  // Direct-dial: e164 derived from the single display override.
  collision: makePhone("collision", collisionRaw, toE164(collisionRaw)),
  calleOcho: makePhone("calleOcho", calleOchoRaw, toE164(calleOchoRaw)),
  shop: makePhone("shop", shopRaw, toE164(shopRaw)),
  parts: makePhone("parts", partsRaw, toE164(partsRaw)),
} satisfies Record<PhoneKey, PhoneNumber>;

export const CONTACT = {
  /** Plain display, e.g. "786-801-0234" */
  phone: displayRaw,
  /** Pretty display, e.g. "(786) 801-0234" */
  phoneFormatted: formatUsPhone(displayRaw),
  // The flat fields below are ALIASES of phones.primary, kept for surfaces not
  // yet migrated to the structured map. They are the same object's fields, so
  // they cannot drift from it.
  /** With country code prefix, e.g. "+1 786-801-0234" */
  phoneWithCountry: phones.primary.display,
  /** Anchor href, e.g. "tel:+17868010234" */
  phoneTelHref: phones.primary.telHref,
  /** E.164 of the dialed number, e.g. "+17868010234" */
  phoneE164: phones.primary.e164,
  /** Structured, keyed access to every public phone number. */
  phones,
} as const;
